import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import type { PersonRef } from '../domain/people';
import { useLoad } from '../hooks/useLoad';
import { useRepositories } from '../hooks/useRepositories';
import { PersonName } from '../components/PersonDialog';
import EndConnectionDialog, { EndConnectionMode } from '../components/EndConnectionDialog';
import { EmptyState, LoadingState, StateForError } from '../components/PageStates';
import UserAvatar from '../components/UserAvatar';
import { fullName } from '../utils/people';
import { Button } from '@/components/ui/button';
import { Users } from 'lucide-react';

/**
 * `/settings/connections` — everyone I'm connected with, visible only to me
 * (015 FR-001). Either of us ending it ends it both ways.
 */
export default function ConnectionsPage() {
  const { audience } = useRepositories();
  const connections = useLoad<PersonRef[]>(() => audience.myFollowing(), 'connections');
  const [ending, setEnding] = useState<{ mode: EndConnectionMode; person: PersonRef } | null>(null);

  let body: React.ReactNode;
  if (connections.status === 'loading') body = <LoadingState label="Loading connections" />;
  else if (connections.status === 'error')
    body = <StateForError error={connections.error} onRetry={connections.reload} />;
  else if (connections.data.length === 0)
    body = (
      <EmptyState
        icon={<Users className="size-8" />}
        title="No connections yet"
        description="Send a group link from your collections to connect with the people you trade with."
        action={
          <Button className="rounded-full" asChild>
            <Link to="/my-collections">Go to your collections</Link>
          </Button>
        }
      />
    );
  else
    body = (
      <ul aria-label="Your connections" className="space-y-3">
        {connections.data.map((person) => {
          const name = fullName(person);
          return (
            <li key={person.id} className="flex items-center gap-4 rounded-3xl bg-card p-5">
              <UserAvatar person={person} className="size-12" textClassName="text-base" />
              <p className="min-w-0 flex-1 font-semibold text-foreground">
                <PersonName person={person} onBlocked={connections.reload} />
              </p>
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  className="rounded-full"
                  aria-label={`Unfollow ${name}`}
                  onClick={() => setEnding({ mode: 'unfollow', person })}
                >
                  Unfollow
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="rounded-full"
                  aria-label={`Remove ${name}`}
                  onClick={() => setEnding({ mode: 'remove', person })}
                >
                  Remove
                </Button>
              </div>
            </li>
          );
        })}
      </ul>
    );

  return (
    <div className="mx-auto max-w-3xl space-y-8">
      <div>
        <h1 className="text-4xl font-bold tracking-tight text-foreground">Connections</h1>
        <p className="mt-2 text-muted-foreground">
          You see each other's open collections. Only you can see this list.
        </p>
      </div>
      {body}
      {ending && (
        <EndConnectionDialog
          mode={ending.mode}
          person={ending.person}
          open
          onOpenChange={(open) => !open && setEnding(null)}
          onDone={connections.reload}
        />
      )}
    </div>
  );
}
